import { useContext, useEffect, useState } from 'react';
import { shallowEqual } from 'react-redux';

import { Box } from '@zillow/constellation-10';

import { ModularListingContext } from '../../../containers/CreateListingPage/context';
import { WizardContext } from '../../../components/Wizard/context';

import { useSelector } from '../../../selectors/useSelector';

import accessUtils from '../../../utils/accessUtils';

import { DISPLAY_SERVER_ERROR_AT } from '../../../constants/WizardConstants';

import SFTotalMonthlyPriceWithFeeAcknowledgementModal from '../../../components/Modal/SFTotalMonthlyPriceWithFeeAcknowledgementModal';
import FormSectionContainer from './components/FormSectionContainer';
import PageWizard from '../../../components/Wizard/PageWizard';
import WizardAlert from '../../../components/WizardAlert';
import WizardTipCardContainer from '../../WizardTipCardContainer';
import WizardTipContent from './components/WizardTipContent';

import { StyledDivCustomLeftSpacing } from '../../../components/StyledComponents/StyledWizardForm';
import { WizardForm } from '../../../styles/wizard';

import type { FormEvent, ReactNode } from 'react';
import type { PageWizardProps } from '../../../components/Wizard/PageWizard';

type Props = {
  onGoToPrevStep: () => void;
  onGoToNextStep: () => void;
  onExitWizard: () => void;
  wizardTitle: ReactNode;
  progressStepper: ReactNode;
  totalSteps: number;
  currentStepIndex: number;
  showDesktopViewBreakpoint: PageWizardProps['showDesktopViewBreakpoint'];
};

const CostsAndFees = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: Props) => {
  const {
    // @ts-expect-error
    wizardState: { isFetchingData },
  } = useContext(WizardContext);

  const {
    modularListingState: { serverErrorObj },
  } = useContext(ModularListingContext);

  const {
    appConfigCanAccessTotalMonthlyPriceWithFee,
    cookieEnabledServices,
    displayTotalMonthlyPriceWithFee,
    isTotalMonthlyPriceWithFeeEligible,
  } = useSelector(
    (state) => ({
      appConfigCanAccessTotalMonthlyPriceWithFee: state.app.appConfig.config.features.canAccessTotalMonthlyPriceWithFee,
      cookieEnabledServices: state.app.cookieEnabledServices,
      displayTotalMonthlyPriceWithFee: state.properties.currentProperty.displayTotalMonthlyPriceWithFee,
      isTotalMonthlyPriceWithFeeEligible: state.properties.currentProperty?.totalMonthlyPriceWithFeeNotice?.isEligible,
    }),
    shallowEqual,
  );

  const [clickedNext, setClickedNext] = useState(false);
  const [clickedBackNext, setClickedBack] = useState(false);
  const [
    displaySFTotalMonthlyPriceWithFeeAcknowledgementModal,
    setDisplaySFTotalMonthlyPriceWithFeeAcknowledgementModal,
  ] = useState(false);

  const canAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    cookieEnabledServices,
    canAccessTotalMonthlyPriceWithFee: appConfigCanAccessTotalMonthlyPriceWithFee,
    isEligible: isTotalMonthlyPriceWithFeeEligible,
  });

  useEffect(() => {
    const { fieldName } = serverErrorObj;

    if (!fieldName && !isFetchingData) {
      checkNavToStep();
    }
  }, [isFetchingData]);

  const checkNavToStep = () => {
    if (clickedNext) {
      onNextClick();
    }

    if (clickedBackNext) {
      onBackClick();
    }
  };

  const onNextClick = () => {
    if (isFetchingData) {
      setClickedNext(true);
      return;
    }

    if (canAccessTotalMonthlyPriceWithFee && !displayTotalMonthlyPriceWithFee) {
      setDisplaySFTotalMonthlyPriceWithFeeAcknowledgementModal(true);

      return;
    }

    onGoToNextStep();
  };

  const onBackClick = () => {
    if (isFetchingData) {
      setClickedBack(true);
      return;
    }

    onGoToPrevStep();
  };

  return (
    <PageWizard
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onNextClick}
      onBackClick={onBackClick}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      {...rest}
    >
      <div data-testid="CostsAndFees">
        <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.PAGE} />

        <Box
          css={{
            display: 'grid',
            gridTemplateColumns: {
              base: 'repeat(6, 1fr)',
              lg: 'repeat(12, 1fr)',
            },
            gap: '400',
          }}
        >
          <Box css={{ gridColumnEnd: { base: 'span 6', lg: 'span 12', xl: 'span 7' } }}>
            {/* cast as div to prevent cannot appear as a descendant of form error */}
            <WizardForm asChild={true} onSubmit={(e: FormEvent<HTMLFormElement>) => e.preventDefault()}>
              <div>
                <FormSectionContainer />
              </div>
            </WizardForm>
          </Box>
          {/* tip component */}
          <Box css={{ gridColumnEnd: { base: 'span 6', lg: 'span 12', xxl: 'span 5' } }}>
            <StyledDivCustomLeftSpacing>
              <WizardTipCardContainer
                header="Tips for adding costs and fees"
                showDesktopViewBreakpoint={showDesktopViewBreakpoint}
              >
                <WizardTipContent />
              </WizardTipCardContainer>
            </StyledDivCustomLeftSpacing>
          </Box>
        </Box>
      </div>

      {displaySFTotalMonthlyPriceWithFeeAcknowledgementModal && (
        <SFTotalMonthlyPriceWithFeeAcknowledgementModal
          onClose={() => setDisplaySFTotalMonthlyPriceWithFeeAcknowledgementModal(false)}
          onSubmit={onGoToNextStep}
        />
      )}
    </PageWizard>
  );
};

export default CostsAndFees;
